import { useCallback, useEffect, useRef, useState } from "react";
import { useUnit } from "effector-react";
import { $audioConfig } from "../state/appConfig";
import { $sceneLoadingState } from "../state/sceneLoading";
import { MUSIC_TRACKS } from "../audio/musicTracks";
import { DEFAULT_ASSISTANT_VOICE_NAME } from "../audio/assistantVoices";
import type { LiveServerMessage, Session } from "@google/genai";
import { connectGeminiLiveTtsSession } from "../services/geminiLiveTts";
import { $locale } from "../state/locale";
import { LOCALE_LANGUAGE_CODES } from "../../server/src/shared/locale";

const MUSIC_FADE_MS = 800;
const DEFAULT_MUSIC_VOLUME = 0.6;
const VOICE_ASSISTANT_STYLE =
  "Read aloud in a dramatic manner, like a history documentary, British accent, like David Attenborough, no audible breathing, clean sound.";
const STREAMING_TTS_MIME = "audio/ogg; codecs=opus";
const SILENT_WAV_DATA_URL =
  "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAIlYAAESsAAACABAAZGF0YQAAAAA=";

const resolveTrack = (trackId: string | null | undefined) =>
  MUSIC_TRACKS.find((track) => track.id === trackId) ?? MUSIC_TRACKS[0] ?? null;

const resolveUrl = (src: string) => {
  if (typeof window === "undefined") {
    return src;
  }
  try {
    return new URL(src, window.location.href).toString();
  } catch {
    return src;
  }
};

const parsePcmSampleRate = (mimeType: string) => {
  const match = mimeType.match(/rate=(\d+)/i);
  if (!match) {
    return null;
  }
  const rate = Number(match[1]);
  return Number.isFinite(rate) && rate > 0 ? rate : null;
};

const isPcmMimeType = (mimeType: string) =>
  /audio\/pcm|audio\/x-pcm|audio\/l16|audio\/raw|pcm|l16/i.test(mimeType);

const clampVolume = (value: number) =>
  Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 1;

const concatChunks = (chunks: Uint8Array[]) => {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const buffer = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    buffer.set(chunk, offset);
    offset += chunk.length;
  }
  return buffer;
};

const base64ToUint8Array = (data: string) => {
  const binary = atob(data);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

export type AudioController = {
  isSpeaking: boolean;
  start: (message: string) => Promise<void>;
  stop: () => void;
};

export function useAudioController(): AudioController {
  const {
    soundOn,
    musicOn,
    voiceOn,
    musicTrackId,
    musicVolume,
    voiceVolume,
    assistantVoiceName,
  } = useUnit($audioConfig);
  const { status: sceneLoadingStatus } = useUnit($sceneLoadingState);
  const locale = useUnit($locale);
  const sceneReady = sceneLoadingStatus === "ready";
  const [isSpeaking, setIsSpeaking] = useState(false);
  const musicAudioRef = useRef<HTMLAudioElement | null>(null);
  const speechAudioRef = useRef<HTMLAudioElement | null>(null);
  const speechUrlRef = useRef<string | null>(null);
  const speechAbortRef = useRef<AbortController | null>(null);
  const fadeRafRef = useRef<number | null>(null);
  const unlockedRef = useRef(false);
  const speechContextRef = useRef<AudioContext | null>(null);
  const speechGainRef = useRef<GainNode | null>(null);
  const speechEndTimerRef = useRef<number | null>(null);
  const speechSessionRef = useRef<Session | null>(null);

  const clearFade = useCallback(() => {
    if (fadeRafRef.current) {
      cancelAnimationFrame(fadeRafRef.current);
      fadeRafRef.current = null;
    }
  }, []);

  const fadeTo = useCallback(
    (
      audio: HTMLAudioElement,
      from: number,
      to: number,
      durationMs: number,
      onComplete?: () => void
    ) => {
      clearFade();
      const start = performance.now();
      const delta = to - from;

      const step = (now: number) => {
        const elapsed = Math.min(now - start, durationMs);
        const progress = durationMs > 0 ? elapsed / durationMs : 1;
        const nextVolume = from + delta * progress;
        audio.volume = Math.min(1, Math.max(0, nextVolume));
        if (progress < 1) {
          fadeRafRef.current = requestAnimationFrame(step);
        } else {
          fadeRafRef.current = null;
          onComplete?.();
        }
      };

      fadeRafRef.current = requestAnimationFrame(step);
    },
    [clearFade]
  );

  const cleanupSpeechUrl = useCallback(() => {
    if (speechUrlRef.current) {
      URL.revokeObjectURL(speechUrlRef.current);
      speechUrlRef.current = null;
    }
  }, []);

  const clearSpeechEndTimer = useCallback(() => {
    if (speechEndTimerRef.current) {
      window.clearTimeout(speechEndTimerRef.current);
      speechEndTimerRef.current = null;
    }
  }, []);

  const stopSpeechContext = useCallback(async () => {
    if (speechContextRef.current) {
      try {
        await speechContextRef.current.close();
      } catch {
        // Ignore close failures.
      }
      speechContextRef.current = null;
      speechGainRef.current = null;
    }
  }, []);

  const unlockAudio = useCallback(async () => {
    if (unlockedRef.current || typeof window === "undefined") {
      return;
    }
    try {
      const audio = new Audio(SILENT_WAV_DATA_URL);
      audio.muted = true;
      await audio.play();
      audio.pause();
      audio.src = "";
      unlockedRef.current = true;
    } catch {
      // Ignore unlock failures; we'll retry on the next gesture.
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const handleUserGesture = () => {
      void unlockAudio();
      if (unlockedRef.current) {
        window.removeEventListener("pointerdown", handleUserGesture);
        window.removeEventListener("touchstart", handleUserGesture);
        window.removeEventListener("keydown", handleUserGesture);
      }
    };

    window.addEventListener("pointerdown", handleUserGesture);
    window.addEventListener("touchstart", handleUserGesture);
    window.addEventListener("keydown", handleUserGesture);

    return () => {
      window.removeEventListener("pointerdown", handleUserGesture);
      window.removeEventListener("touchstart", handleUserGesture);
      window.removeEventListener("keydown", handleUserGesture);
    };
  }, [unlockAudio]);

  const stop = useCallback(() => {
    speechAbortRef.current?.abort();
    speechAbortRef.current = null;
    speechSessionRef.current?.close();
    speechSessionRef.current = null;
    clearSpeechEndTimer();
    const audio = speechAudioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      audio.onended = null;
    }
    cleanupSpeechUrl();
    void stopSpeechContext();
    setIsSpeaking(false);
  }, [cleanupSpeechUrl, clearSpeechEndTimer, stopSpeechContext]);

  const start = useCallback(
    async (message: string) => {
      const text = message.trim();
      if (!text) {
        return;
      }
      if (!soundOn || !voiceOn) {
        return;
      }

      stop();
      const controller = new AbortController();
      speechAbortRef.current = controller;
      setIsSpeaking(true);

      try {
        await unlockAudio();
        const audio = speechAudioRef.current ?? new Audio();
        speechAudioRef.current = audio;
        audio.volume = clampVolume(voiceVolume);
        cleanupSpeechUrl();
        let mode: "pcm" | "mediasource" | "buffer" | null = null;
        let mimeType: string | null = null;
        let hasAudio = false;
        let streamDone = false;
        let finalized = false;
        let scheduledTime = 0;
        const bufferedChunks: Uint8Array[] = [];
        const mediaQueue: Array<Uint8Array<ArrayBuffer>> = [];
        let mediaSource: MediaSource | null = null;
        let sourceBuffer: SourceBuffer | null = null;
        let mediaStarted = false;

        const flushQueue = () => {
          if (!sourceBuffer || sourceBuffer.updating) {
            return;
          }
          const next = mediaQueue.shift();
          if (next) {
            sourceBuffer.appendBuffer(next);
            return;
          }
          if (streamDone && mediaSource?.readyState === "open") {
            mediaSource.endOfStream();
          }
        };

        const ensureMediaSource = async (nextMimeType: string) => {
          if (mediaSource) {
            return;
          }
          if (
            typeof MediaSource === "undefined" ||
            !MediaSource.isTypeSupported(nextMimeType)
          ) {
            mode = "buffer";
            return;
          }
          mode = "mediasource";
          mediaSource = new MediaSource();
          const url = URL.createObjectURL(mediaSource);
          speechUrlRef.current = url;
          audio.src = url;
          audio.onended = () => {
            setIsSpeaking(false);
            cleanupSpeechUrl();
          };

          await new Promise<void>((resolve) => {
            mediaSource?.addEventListener("sourceopen", () => resolve(), {
              once: true,
            });
          });

          sourceBuffer = mediaSource.addSourceBuffer(nextMimeType);
          sourceBuffer.addEventListener("updateend", () => {
            if (!mediaStarted) {
              mediaStarted = true;
              void audio.play();
            }
            flushQueue();
          });
          flushQueue();
        };

        const playBufferedAudio = async (finalMimeType: string) => {
          if (!bufferedChunks.length) {
            setIsSpeaking(false);
            return;
          }
          const merged = concatChunks(bufferedChunks);
          const blob = new Blob([merged], { type: finalMimeType });
          const url = URL.createObjectURL(blob);
          speechUrlRef.current = url;
          audio.src = url;
          audio.load();
          audio.onended = () => {
            setIsSpeaking(false);
            cleanupSpeechUrl();
          };
          await audio.play();
        };

        const finalize = async () => {
          if (finalized) {
            return;
          }
          finalized = true;
          streamDone = true;
          speechSessionRef.current?.close();
          speechSessionRef.current = null;

          if (!hasAudio) {
            setIsSpeaking(false);
            return;
          }

          if (mode === "pcm") {
            const context = speechContextRef.current;
            if (!context) {
              setIsSpeaking(false);
              return;
            }
            const remaining =
              Math.max(0, scheduledTime - context.currentTime) * 1000;
            clearSpeechEndTimer();
            speechEndTimerRef.current = window.setTimeout(() => {
              setIsSpeaking(false);
              void stopSpeechContext();
            }, remaining);
            return;
          }

          if (mode === "mediasource") {
            flushQueue();
            return;
          }

          await playBufferedAudio(mimeType ?? STREAMING_TTS_MIME);
        };

        const handleAudioChunk = async (
          chunk: Uint8Array,
          chunkMimeType?: string
        ) => {
          if (controller.signal.aborted) {
            return;
          }
          if (!chunk.byteLength) {
            return;
          }
          hasAudio = true;
          if (!mimeType && chunkMimeType) {
            mimeType = chunkMimeType;
          }
          const effectiveMimeType =
            mimeType ?? chunkMimeType ?? STREAMING_TTS_MIME;

          if (!mode) {
            mode = isPcmMimeType(effectiveMimeType) ? "pcm" : "mediasource";
          }

          if (mode === "pcm") {
            const sampleRate = parsePcmSampleRate(effectiveMimeType) ?? 24000;
            const context =
              speechContextRef.current ?? new AudioContext({ sampleRate });
            speechContextRef.current = context;
            if (context.state === "suspended") {
              await context.resume();
            }
            clearSpeechEndTimer();

            let gain = speechGainRef.current;
            if (!gain || gain.context !== context) {
              gain?.disconnect();
              gain = context.createGain();
              gain.connect(context.destination);
              speechGainRef.current = gain;
            }
            gain.gain.value = clampVolume(voiceVolume);

            if (scheduledTime === 0) {
              scheduledTime = context.currentTime;
            }
            const frameCount = Math.floor(chunk.byteLength / 2);
            if (!frameCount) {
              return;
            }
            const audioBuffer = context.createBuffer(1, frameCount, sampleRate);
            const channel = audioBuffer.getChannelData(0);
            for (let i = 0; i < frameCount; i += 1) {
              const lo = chunk[i * 2] ?? 0;
              const hi = chunk[i * 2 + 1] ?? 0;
              let sample = (hi << 8) | lo;
              if (sample & 0x8000) {
                sample = sample - 0x10000;
              }
              channel[i] = sample / 32768;
            }
            const source = context.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(gain);
            if (scheduledTime < context.currentTime) {
              scheduledTime = context.currentTime;
            }
            source.start(scheduledTime);
            scheduledTime += audioBuffer.duration;
            return;
          }

          if (mode === "mediasource") {
            await ensureMediaSource(effectiveMimeType);
          }

          if (mode === "buffer") {
            bufferedChunks.push(chunk);
            return;
          }

          if (mode === "mediasource") {
            const safeChunk = new Uint8Array(chunk);
            mediaQueue.push(safeChunk);
            flushQueue();
            return;
          }

          bufferedChunks.push(chunk);
        };

        const handleServerMessage = async (message: LiveServerMessage) => {
          if (finalized || controller.signal.aborted) {
            return;
          }
          if (message.serverContent?.interrupted) {
            stop();
            return;
          }
          const parts = message.serverContent?.modelTurn?.parts ?? [];
          if (import.meta.env.DEV) {
            const partSummaries = parts.map((part) => ({
              hasInlineData: Boolean(part.inlineData?.data),
              mimeType: part.inlineData?.mimeType,
              hasText: Boolean(part.text),
            }));
            console.log("[Live TTS] server message", {
              hasServerContent: Boolean(message.serverContent),
              partSummaries,
              turnComplete: message.serverContent?.turnComplete,
              generationComplete: message.serverContent?.generationComplete,
            });
          }
          for (const part of parts) {
            const inlineData = part.inlineData;
            if (!inlineData?.data) {
              continue;
            }
            const partMimeType = inlineData.mimeType;
            if (partMimeType && !partMimeType.startsWith("audio/")) {
              continue;
            }
            const chunk = base64ToUint8Array(inlineData.data);
            await handleAudioChunk(chunk, partMimeType);
          }

          if (
            message.serverContent?.turnComplete ||
            message.serverContent?.generationComplete
          ) {
            await finalize();
          }
        };

        const session = await connectGeminiLiveTtsSession({
          voiceName: assistantVoiceName?.trim() || DEFAULT_ASSISTANT_VOICE_NAME,
          languageCode: LOCALE_LANGUAGE_CODES[locale],
          signal: controller.signal,
          onMessage: (message) => {
            void handleServerMessage(message);
          },
          onError: () => {
            if (!finalized) {
              stop();
            }
          },
          onClose: () => {
            if (!finalized) {
              void finalize();
            }
          },
        });

        if (controller.signal.aborted) {
          session.close();
          return;
        }

        const ttsPrompt = `${VOICE_ASSISTANT_STYLE} Speak the following text verbatim and return audio only. Transcript: ${text}`;
        if (import.meta.env.DEV) {
          console.log("[Live TTS] sending prompt", {
            textLength: text.length,
          });
        }
        speechSessionRef.current = session;
        session.sendClientContent({
          turns: [{ role: "user", parts: [{ text: ttsPrompt }] }],
          turnComplete: true,
        });
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setIsSpeaking(false);
      }
    },
    [
      cleanupSpeechUrl,
      clearSpeechEndTimer,
      voiceVolume,
      soundOn,
      stop,
      stopSpeechContext,
      unlockAudio,
      voiceOn,
      assistantVoiceName,
      locale,
    ]
  );

  useEffect(() => {
    const nextVolume = clampVolume(voiceVolume);
    const audio = speechAudioRef.current;
    if (audio) {
      audio.volume = nextVolume;
    }
    if (speechGainRef.current) {
      speechGainRef.current.gain.value = nextVolume;
    }
  }, [voiceVolume]);

  useEffect(() => {
    if (!soundOn || !voiceOn) {
      stop();
    }
  }, [stop, soundOn, voiceOn]);

  useEffect(() => {
    const audio = musicAudioRef.current ?? new Audio();
    musicAudioRef.current = audio;
    audio.loop = true;
    audio.preload = "auto";

    return () => {
      clearFade();
      audio.pause();
      audio.src = "";
    };
  }, [clearFade]);

  useEffect(() => {
    const audio = musicAudioRef.current;
    if (!audio || typeof window === "undefined") {
      return;
    }

    const track = resolveTrack(musicTrackId);
    if (!track) {
      if (!audio.paused) {
        fadeTo(audio, audio.volume, 0, MUSIC_FADE_MS, () => audio.pause());
      }
      return;
    }

    const targetVolume = clampVolume(
      (track.volume ?? DEFAULT_MUSIC_VOLUME) * clampVolume(musicVolume)
    );
    const desiredSrc = resolveUrl(track.src);
    const currentSrc = audio.src ? resolveUrl(audio.src) : "";
    const hasNewTrack = currentSrc !== desiredSrc;

    const startPlayback = async () => {
      audio.src = track.src;
      audio.currentTime = 0;
      audio.volume = 0;
      try {
        await audio.play();
        fadeTo(audio, 0, targetVolume, MUSIC_FADE_MS);
      } catch {
        audio.pause();
      }
    };

    if (!sceneReady || !soundOn || !musicOn) {
      if (!audio.paused) {
        fadeTo(audio, audio.volume, 0, MUSIC_FADE_MS, () => audio.pause());
      }
      return;
    }

    if (hasNewTrack) {
      if (!audio.paused) {
        fadeTo(audio, audio.volume, 0, MUSIC_FADE_MS, () => {
          audio.pause();
          startPlayback();
        });
      } else {
        void startPlayback();
      }
      return;
    }

    if (audio.paused) {
      void startPlayback();
    } else {
      fadeTo(audio, audio.volume, targetVolume, MUSIC_FADE_MS);
    }
  }, [fadeTo, musicOn, musicTrackId, musicVolume, sceneReady, soundOn]);

  return { isSpeaking, start, stop };
}
